import { useEffect, useRef, useState } from 'react';
import { Form } from 'antd';
import { parsePhoneNumber } from 'react-phone-number-input';
import { toast } from 'react-hot-toast';
import { getCurrentUser } from '../utils/userStorage';
import { formatTime } from '../utils/formatTime';
import createCall from '../utils/call/createCall';
import connectCall from '../utils/call/connectCall';
import { useTimer } from './useTimer';
import { CALLS_COUNTRIES } from '../utils/supportedFeature';

export default function useCallDialer({ patient, onCallEnded }) {
  const [form] = Form.useForm();
  const [rawNumber, setRawNumber] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [callStatus, setCallStatus] = useState('idle');
  const [activeCall, setActiveCall] = useState(null);
  const [isCallModalVisible, setIsCallModalVisible] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [activeCallTarget, setActiveCallTarget] = useState('');
  const didAutoDial = useRef(false);
  const currentUser = getCurrentUser();
  const defaultCountryRaw = currentUser?.calls_country || currentUser?.survey_country || 'MX';
  const defaultCountry = CALLS_COUNTRIES.includes(defaultCountryRaw.toUpperCase())
    ? defaultCountryRaw.toUpperCase()
    : 'MX';
  const rawTimer = useTimer(callStatus === 'connected', hasStarted);
  const timer = formatTime(rawTimer || 0);

  const patientPhone = patient?.getMobileNumber() ?? null;
  const patientUuid = patient?.getUUID() ?? null;

  function validateAndParse(raw) {
    if (!raw) return null;
    const cleaned = raw.replace(/[\s\-\.]/g, '');
    try {
      const parsed = parsePhoneNumber(cleaned, defaultCountry);
      return parsed.isValid() ? parsed : null;
    } catch {
      return null;
    }
  }

  async function startCall(parsed) {
    const e164 = parsed.number;
    setCallStatus('connecting');
    setIsCallModalVisible(true);
    setActiveCallTarget(parsed.formatInternational());

    try {
      const { twilio_token, call_uuid } = await createCall(e164, patientUuid);
      const call = await connectCall({ twilioToken: twilio_token, callUUID: call_uuid, parsedNumber: parsed });
      call.on('disconnect', endCall);
      call.on('cancel', endCall);
      setActiveCall(call);
      setCallStatus('connected');
    } catch (err) {
      console.error('[startCall] error:', err);
      toast.error('Error al iniciar la llamada');
      setCallStatus('idle');
      setIsCallModalVisible(false);
    } finally {
      setHasStarted(true);
    }
  }

  function endCall() {
    if (activeCall) {
      activeCall.off('disconnect', endCall);
      activeCall.off('cancel', endCall);
      activeCall.disconnect();
    }
    setActiveCall(null);
    setCallStatus('idle');
    setIsCallModalVisible(false);
    setHasStarted(false);
    onCallEnded?.();
  }

  function handlePhoneChange(val) {
    setRawNumber(val);
    form.setFieldsValue({ to_number: val });
    setPhoneError('');
  }

  function handleSubmit() {
    const raw = form.getFieldValue('to_number') || '';
    const parsed = validateAndParse(raw);
    if (!parsed) {
      setPhoneError('Número no válido');
      return;
    }
    setPhoneError('');
    startCall(parsed);
  }

  useEffect(() => {
    if (!patientPhone || didAutoDial.current) return;
    const parsed = validateAndParse(patientPhone);
    if (!parsed) {
      toast.error('Número del paciente no válido');
      setTimeout(() => onCallEnded?.(), 0);
      return;
    }
    didAutoDial.current = true;
    startCall(parsed);
  }, [patientPhone]);

  return {
    form,
    ui: {
      rawNumber,
      phoneError,
      callStatus,
      isCallModalVisible,
      timer,
      activeCallTarget,
      defaultCountry,
    },
    handlers: {
      handlePhoneChange,
      handleSubmit,
      endCall,
    },
  };
}